import React, { useState, useEffect } from "react";
import { FaHeart, FaComment } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n";
import { getLocalizedBlog } from "../utils/localizedBlog";
import { getOptimizedPublicImage, hasOptimizedPublicImage } from "../utils/imageOptimization";
import { fetchJsonFromApiCandidates } from "../utils/api";
import { mergeBlogsWithFallbacks, isFallbackBlog } from "../utils/fallbackBlogs";
import { renderBrandText } from "../utils/brandText";
import blogFallbackAvatar from "../images/blog-fallback-avatar.png";
import "./BlogDetails.css";

const fallbackTabBlogs = mergeBlogsWithFallbacks([]);

function BlogDetails() {
    const { language, t } = useTranslation();
    const [blogs, setBlogs] = useState(fallbackTabBlogs);
    const [showCommentBox, setShowCommentBox] = useState({});
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [newComment, setNewComment] = useState("");
    const navigate = useNavigate(); // Hook para navegación

    useEffect(() => {
        fetchJsonFromApiCandidates("/api/blogs", { timeoutMs: 2500 })
            .then(({ data }) => setBlogs(mergeBlogsWithFallbacks(data)))
            .catch((error) => {
                console.error("Error al cargar blogs:", error);
                setBlogs(fallbackTabBlogs);
            });
    }, []);

    const handleLike = async (id) => {
        if (isFallbackBlog(id)) {
            setBlogs((currentBlogs) =>
                currentBlogs.map((blog) =>
                    blog._id === id ? { ...blog, likes: (blog.likes || 0) + 1 } : blog
                )
            );
            return;
        }

        try {
            const { data: updatedBlog } = await fetchJsonFromApiCandidates(`/api/blogs/${id}/like`, { method: "POST" });
            setBlogs(blogs.map(blog => (blog._id === id ? updatedBlog : blog)));
        } catch (error) {
            console.error("Error al dar like:", error);
        }
    };

    const toggleCommentBox = (id) => {
        setShowCommentBox({ ...showCommentBox, [id]: !showCommentBox[id] });
    };

    const handleAddComment = async (id) => {
        if (!name.trim() || !email.trim() || !newComment.trim()) return;

        if (isFallbackBlog(id)) {
            setBlogs((currentBlogs) =>
                currentBlogs.map((blog) =>
                    blog._id === id
                        ? {
                            ...blog,
                            comments: [
                                ...(Array.isArray(blog.comments) ? blog.comments : []),
                                { name, email, comment: newComment }
                            ]
                        }
                        : blog
                )
            );
            setName("");
            setEmail("");
            setNewComment("");
            setShowCommentBox({ ...showCommentBox, [id]: false });
            return;
        }
    
        try {
            await fetchJsonFromApiCandidates(`/api/blogs/${id}/comment`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, comment: newComment })
            });

            const { data: updatedBlogs } = await fetchJsonFromApiCandidates("/api/blogs");
            setBlogs(Array.isArray(updatedBlogs) ? updatedBlogs : []);
            
            setName("");
            setEmail("");
            setNewComment("");
            setShowCommentBox({ ...showCommentBox, [id]: false });
        } catch (error) {
            console.error("Error al añadir comentario:", error);
        }
    };

    const handleNavigate = (id) => {
        navigate(`/blog/${id}`); // Navegar a la página del blog seleccionado
    };

    const BlogImage = ({ blog, title }) => {
        if (!blog.image) {
            return (
                <div className="blog-image blog-image-placeholder" aria-label={title}>
                    <img src={blogFallbackAvatar} alt={title} className="blog-image-placeholder-avatar" loading="lazy" />
                </div>
            );
        }

        const optimizedImage = getOptimizedPublicImage(blog.image);
        const image = (
            <img
                src={blog.image}
                alt={title}
                className="blog-image"
                loading="lazy"
                decoding="async"
                width="900"
                height="506"
            />
        );

        if (!hasOptimizedPublicImage(blog.image)) return image;

        return (
            <picture>
                <source srcSet={optimizedImage} type="image/webp" />
                {image}
            </picture>
        );
    };

    return (
        <section id="blog-posts" className="blog-container">
            <h2 className="blogs-title">{t("blog.latestTitle")}</h2>
            <p className="blogs-info">
                {renderBrandText(t("blog.info"))}
            </p>
            <div className="blog-grid">
                {blogs.length > 0 ? (
                    blogs.map((blog) => {
                        const localizedBlog = getLocalizedBlog(blog, language);

                        return (
                        <div className="blog-card" key={blog._id}>
                            <BlogImage blog={blog} title={localizedBlog.title} />
                            <div className="blog-content-tab">
                                <h3>{localizedBlog.title}</h3>
                                <p className="blog-date-tab">{t("common.publishedOn")} {new Date(blog.createdAt).toLocaleDateString()}</p>
                                <p className="blog-summary">{localizedBlog.summary}</p>
                                <div className="blog-actions">
                                    <button className="like-button" onClick={() => handleLike(blog._id)}>
                                        <FaHeart /> <span>{blog.likes} {t("common.likes")}</span>
                                    </button>
                                    <button className="comment-button" onClick={() => toggleCommentBox(blog._id)}>
                                        <FaComment /> {t("common.comments")}
                                    </button>
                                </div>

                                {/* Sección de comentarios */}
                                {showCommentBox[blog._id] && (
                                    <div className="comment-box">
                                        <input 
                                            type="text" 
                                            placeholder={t("blog.namePlaceholder")} 
                                            value={name} 
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                        <input 
                                            type="email" 
                                            placeholder={t("blog.emailPlaceholder")} 
                                            value={email} 
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                        <input 
                                            type="text" 
                                            placeholder={t("blog.commentPlaceholder")} 
                                            value={newComment} 
                                            onChange={(e) => setNewComment(e.target.value)}
                                        />
                                        <button onClick={() => handleAddComment(blog._id)}>{t("common.send")}</button>
                                    </div>
                                )}

                                {/* Mostrar comentarios */}
                                <div className="comments-list">
                                    {blog.comments && blog.comments.length > 0 ? (
                                        blog.comments.map((comment, index) => (
                                            <div key={index} className="comment">
                                                <p><strong>{comment.user}</strong> ({comment.email}): {comment.comment}</p>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="no-comments">{t("blog.noComments")}</p>
                                    )}
                                </div>

                                <button className="read-more" onClick={() => handleNavigate(blog._id)}>
                                    {t("common.readMore")}
                                </button>
                            </div>
                        </div>
                    )})
                ) : (
                    <p className="loading">{t("common.loadingBlogs")}</p>
                )}
            </div>
        </section>
    );
}

export default BlogDetails;
